/**
 * Product Tier Service
 * 
 * Manages tier-based feature access and resolution for the 3-tier product model.
 * Determines which features are available based on the user's subscription tier.
 * 
 * @package @flowstate/core
 * @version 1.0.0
 * @white-label-ready
 */

import { logger } from '../../lib/logger';
import type {
  IService,
  ProductTier,
  TierFeature,
  TierContext,
  TierAccessResult,
  ProductTierConfig,
} from '@flowstate/types';
import {
  PRODUCT_TIER_CONFIGS,
  checkTierAccess,
  isTierFeatureEnabled,
  getProductTierConfig,
} from '@flowstate/types';

// ============================================================================
// Product Tier Service
// ============================================================================

export class ProductTierService implements IService {
  readonly name = 'ProductTierService';

  private currentTier: ProductTier = 'personal';
  private organizationId: string | null = null;

  constructor() {
    logger.info('📝 ProductTierService created');
  }

  /**
   * Initialize the service
   */
  async initialize(): Promise<void> {
    logger.info('🚀 ProductTierService initializing...');
    logger.info('🚀 ProductTierService initialized');
  }

  /**
   * Dispose the service
   */
  async dispose(): Promise<void> {
    logger.info('🛑 ProductTierService disposing...');
    this.currentTier = 'personal';
    this.organizationId = null;
    logger.info('🛑 ProductTierService disposed');
  }

  // ==========================================================================
  // Tier Resolution
  // ==========================================================================

  /**
   * Resolve the product tier from a subscription_tier string.
   * Maps legacy tier names (free/pro/enterprise) to the new product tiers.
   */
  resolveProductTier(subscriptionTier: string | null | undefined): ProductTier {
    if (!subscriptionTier) return 'personal';

    const tierMap: Record<string, ProductTier> = {
      // New tier names
      personal: 'personal',
      team: 'team',
      self_hosted: 'self_hosted',
      // Legacy tier names mapped to new model
      free: 'personal',
      pro: 'team',
      enterprise: 'self_hosted',
    };

    return tierMap[subscriptionTier.toLowerCase()] ?? 'personal';
  }

  /**
   * Set the current tier context
   */
  setTierContext(tier: ProductTier, organizationId?: string | null): void {
    this.currentTier = tier;
    this.organizationId = organizationId ?? null;
    logger.info(`🎯 Tier context set: ${tier} (org: ${organizationId ?? 'none'})`);
  }

  /**
   * Build a full tier context object for the current session
   */
  buildTierContext(
    subscriptionTier: string | null | undefined,
    organizationId?: string | null
  ): TierContext {
    const tier = this.resolveProductTier(subscriptionTier);
    const config = getProductTierConfig(tier);

    return {
      tier,
      organizationId: organizationId ?? null,
      dashboardType: config.dashboardType,
      isSelfHosted: config.deployment === 'self_hosted',
      features: config.features,
      limits: config.limits,
    };
  }

  // ==========================================================================
  // Feature Access
  // ==========================================================================

  /**
   * Check if a feature is enabled for the current tier
   */
  isFeatureEnabled(feature: TierFeature): boolean {
    return isTierFeatureEnabled(this.currentTier, feature);
  }

  /**
   * Check feature access with detailed result
   */
  checkAccess(feature: TierFeature): TierAccessResult {
    return checkTierAccess(this.currentTier, feature);
  }

  /**
   * Check feature access for a specific tier
   */
  checkAccessForTier(tier: ProductTier, feature: TierFeature): TierAccessResult {
    return checkTierAccess(tier, feature);
  }

  // ==========================================================================
  // Tier Config
  // ==========================================================================

  /**
   * Get the current product tier
   */
  getCurrentTier(): ProductTier {
    return this.currentTier;
  }

  /**
   * Get the config for the current tier
   */
  getCurrentTierConfig(): ProductTierConfig {
    return getProductTierConfig(this.currentTier);
  }

  /**
   * Get the dashboard type for the current tier
   */
  getDashboardType(): 'personal' | 'team' {
    return getProductTierConfig(this.currentTier).dashboardType;
  }

  /**
   * Get all available tier configs (useful for pricing pages)
   */
  getAllTierConfigs(): Record<ProductTier, ProductTierConfig> {
    return PRODUCT_TIER_CONFIGS;
  }
}

// Singleton export
export const productTierService = new ProductTierService();
